/**
 * Component for editing quiz settings including availability,
 * behavior, reporting, and approval options.
 *
 * @module components/quizzes/edit-quiz-availability
 */

import type { Quiz } from '@efdigital/ll-addon-data';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

import { type EditRecordSignature } from '../edit-record-base.gts';
import { EditRecordCollapse } from '../edit-record-collapse.gts';
import { InputDateTime } from '../input-date.gts';

export class EditQuizAvailability extends Component<EditRecordSignature<Quiz>> {
  get startsDate(): string {
    return this.args.item.starts ?? '';
  }

  get endsDate(): string {
    return this.args.item.ends ?? '';
  }

  setStarts = (value: string) => {
    this.args.item.starts = value || null;
  };

  setEnds = (value: string) => {
    this.args.item.ends = value || null;
  };

  get needsAssignment(): boolean {
    return this.args.item.needsAssignment ?? false;
  }

  toggleNeedsAssignment = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.needsAssignment = target.checked;
  };

  <template>
    <EditRecordCollapse
      @editLabel="Edit Availability"
      @item={{@item}}
      @context={{@context}}
      @maxDepth={{1}}
      @collapseLabel="Availability"
      ...attributes
    >
      <:help>
        <p class="form-text">
          Control when participants can access and complete the quiz.
        </p>
      </:help>
      <:form as |uniqueId isDisabled|>
        {{! Availability Section }}
        <fieldset disabled={{isDisabled}} class="mb-4">
          <div class="row">
            <div class="col-md-6 mb-3">
              <label for="{{uniqueId}}-starts" class="form-label">
                Opens
              </label>
              <InputDateTime
                id="{{uniqueId}}-starts"
                name="starts"
                class="form-control"
                @value={{this.startsDate}}
                @onChange={{this.setStarts}}
              />
              <div class="form-text">Leave blank to open immediately.</div>
            </div>

            <div class="col-md-6 mb-3">
              <label for="{{uniqueId}}-ends" class="form-label">
                Closes
              </label>
              <InputDateTime
                id="{{uniqueId}}-ends"
                name="ends"
                class="form-control"
                @value={{this.endsDate}}
                @onChange={{this.setEnds}}
              />
              <div class="form-text">Leave blank to never close.</div>
            </div>
          </div>

          <div class="form-check mb-3">
            <input
              type="checkbox"
              class="form-check-input"
              id="{{uniqueId}}-needs-assignment"
              checked={{this.needsAssignment}}
              {{on "change" this.toggleNeedsAssignment}}
            />
            <label class="form-check-label" for="{{uniqueId}}-needs-assignment">
              Require assignment
            </label>
            <div class="form-text">
              Only assigned users can access this quiz.
            </div>
          </div>
        </fieldset>
      </:form>
    </EditRecordCollapse>
  </template>
}
